import { useEffect, useState } from "react";
import Modal from "@mui/material/Modal";
import Autocomplete from "@mui/material/Autocomplete";
import { TextField } from "@mui/material";
import { IoCloseOutline } from "react-icons/io5";
import { useTranslation } from "react-i18next";
import { useFormat } from "../../../../../../../../i18n/format";
import {
  fetchAllRoles,
  updateShipperUserData,
} from "../../../../../../../../services/settings.services";
import Countries from "../../../../../../../../data/countries";
import BoltButton from "../../../../../../../../Components/UI/Buttons/BoltButton";

const inputCls =
  "w-full h-10 px-3 border border-ui-border rounded-lg text-sm text-ui-text outline-none focus:border-brand focus:ring-1 focus:ring-brand-light transition-all";

const autocompleteSx = {
  "& .MuiOutlinedInput-root": {
    height: "40px",
    padding: "0 9px",
    fontSize: "14px",
    borderRadius: "8px",
    "& fieldset": { borderColor: "var(--color-ui-border)" },
    "&:hover fieldset": { borderColor: "var(--color-ui-border)" },
    "&.Mui-focused fieldset": { borderColor: "#60a5fa", borderWidth: "1px" },
  },
  "& .MuiAutocomplete-input": { padding: "0 !important" },
};

export default function EditShipperUser({ open, setOpen, previousData, setUsers, setSnack }) {
  const { t } = useTranslation("settings");
  const { formatCountry } = useFormat();
  const [data, setData] = useState({});
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleClose = () => setOpen(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData((d) => ({ ...d, [name]: value }));
  };

  const handleFieldChange = (field, value) => {
    if (field === "role") {
      const matched = roles.find((r) => r.name === value);
      setData((d) => ({
        ...d,
        role: matched?.name ?? "",
        roles: matched ? [matched] : [],
      }));
    } else {
      setData((d) => ({ ...d, [field]: value }));
    }
  };

  const handleUpdate = async () => {
    setLoading(true);
    const editableFields = [
      "firstname",
      "lastname",
      "username",
      "email",
      "phone_number",
      "address",
      "country",
      "province",
      "city",
      "zip",
    ];
    const payload = editableFields.reduce((acc, key) => {
      if (data[key] !== undefined) acc[key] = data[key];
      return acc;
    }, {});
    payload.permissions = data.permissions?.map((p) => p.id) ?? [];
    payload.roles = data.roles?.map((r) => r.id) ?? [];

    const response = await updateShipperUserData(payload, data.id);
    setLoading(false);
    if (!response?.data?.success) {
      const errData = response?.error?.response?.data ?? {};
      const fieldErrors = Object.entries(errData)
        .filter(([key]) => !["success", "message", "request"].includes(key))
        .flatMap(([, msgs]) => msgs)
        .join(" ");
      setSnack((s) => ({ ...s, open: true, text: fieldErrors || errData?.message || t("messages.somethingWentWrong"), severity: "error" }));
    } else {
      setUsers((prev) => {
        const list = Array.isArray(prev) ? prev : prev?.data ?? [];
        return Array.isArray(prev)
          ? list.map((u) => (u.id === data.id ? response.data.result : u))
          : { ...prev, data: list.map((u) => (u.id === data.id ? response.data.result : u)) };
      });
      handleClose();
      setSnack((s) => ({ ...s, open: true, text: t("helperUsers.updated"), severity: "success" }));
    }
  };

  useEffect(() => {
    if (previousData) setData({ ...previousData, logo_url: null });
  }, [previousData]);

  useEffect(() => {
    fetchAllRoles().then((saved) => {
      setRoles(saved?.data?.result?.data?.map((r) => ({ id: r.id, name: r.name })) ?? []);
    });
  }, []);

  return (
    <Modal open={open} onClose={handleClose}>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-ui-surface rounded-xl shadow-2xl w-[680px] max-w-[95vw] max-h-[90vh] flex flex-col outline-none">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-ui-surface-sunken">
          <h2 className="text-lg font-semibold text-ui-text m-0 leading-none">{t("helperUsers.editTitle")}</h2>
          <button onClick={handleClose} className="text-ui-text-subtle hover:text-ui-text-muted transition-colors">
            <IoCloseOutline size={22} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-6 py-5 flex-1">
          <div className="grid grid-cols-2 gap-x-4 gap-y-4">

            <div>
              <label className="block text-xs font-medium text-ui-text-muted mb-1">{t("fields.firstName")}</label>
              <input className={inputCls} placeholder={t("fields.firstName")} name="firstname" value={data.firstname ?? ""} onChange={handleChange} />
            </div>
            <div>
              <label className="block text-xs font-medium text-ui-text-muted mb-1">{t("fields.lastName")}</label>
              <input className={inputCls} placeholder={t("fields.lastName")} name="lastname" value={data.lastname ?? ""} onChange={handleChange} />
            </div>

            <div>
              <label className="block text-xs font-medium text-ui-text-muted mb-1">{t("fields.username")}</label>
              <input className={inputCls} placeholder={t("fields.username")} name="username" value={data.username ?? ""} onChange={handleChange} />
            </div>
            <div>
              <label className="block text-xs font-medium text-ui-text-muted mb-1">{t("fields.email")}</label>
              <input className={inputCls} placeholder={t("fields.email")} name="email" type="email" value={data.email ?? ""} onChange={handleChange} />
            </div>

            <div>
              <label className="block text-xs font-medium text-ui-text-muted mb-1">{t("fields.phoneNumber")}</label>
              <input className={inputCls} placeholder={t("fields.phoneNumber")} name="phone_number" type="number" value={data.phone_number ?? ""} onChange={handleChange} />
            </div>
            <div>
              <label className="block text-xs font-medium text-ui-text-muted mb-1">{t("fields.role")}</label>
              <Autocomplete
                freeSolo
                options={roles.map((r) => r.name)}
                value={data.roles?.[0]?.name ?? data.role ?? null}
                sx={autocompleteSx}
                renderInput={(params) => <TextField {...params} placeholder={t("fields.selectRole")} />}
                onChange={(_, role) => handleFieldChange("role", role)}
              />
            </div>

            <div className="col-span-2">
              <label className="block text-xs font-medium text-ui-text-muted mb-1">{t("fields.address")}</label>
              <input className={inputCls} placeholder={t("fields.address")} name="address" value={data.address ?? ""} onChange={handleChange} />
            </div>

            <div>
              <label className="block text-xs font-medium text-ui-text-muted mb-1">{t("fields.country")}</label>
              <Autocomplete
                disablePortal
                options={Countries}
                value={Countries.find((c) => c.name === data.country) || null}
                getOptionLabel={(o) => formatCountry(o.code, o.name)}
                sx={autocompleteSx}
                renderInput={(params) => <TextField {...params} placeholder={t("fields.country")} />}
                onChange={(_, country) => handleFieldChange("country", country?.name ?? "")}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-ui-text-muted mb-1">{t("fields.province")}</label>
              <input className={inputCls} placeholder={t("fields.province")} name="province" value={data.province ?? ""} onChange={handleChange} />
            </div>

            <div>
              <label className="block text-xs font-medium text-ui-text-muted mb-1">{t("fields.city")}</label>
              <input className={inputCls} placeholder={t("fields.city")} name="city" value={data.city ?? ""} onChange={handleChange} />
            </div>
            <div>
              <label className="block text-xs font-medium text-ui-text-muted mb-1">{t("fields.postalZip")}</label>
              <input className={inputCls} placeholder={t("fields.postalZip")} name="zip" value={data.zip ?? ""} onChange={handleChange} />
            </div>

          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-6 py-3 border-t border-ui-surface-sunken">
          <BoltButton label={t("common:actions.cancel")} variant="outline" size="md" onClick={handleClose} />
          <BoltButton label={t("helperUsers.updateUser")} variant="primary" size="md" loading={loading} onClick={handleUpdate} />
        </div>
      </div>
    </Modal>
  );
}
